/**
 * GAMES CONFIGURATION UTILITIES
 * ==============================
 * This file provides utility functions for accessing and assembling game configurations.
 * All base configuration data now comes from the unified gameConfig.js file (Single Source of Truth).
 * This file is responsible for deriving configurations and assembling full configs for different use cases.
 */

import { UNIFIED_GAMES_CONFIG } from '../gameConfig';

// Sort games alphabetically by title for consistent display
const SORTED_GAMES_CONFIG = [...UNIFIED_GAMES_CONFIG].sort((a, b) => a.title.localeCompare(b.title));

/**
 * Get all games configuration (basic data for lists, navigation, etc.)
 * @returns {Array} Array of game configuration objects
 */
export const getGamesConfig = () => SORTED_GAMES_CONFIG;

/**
 * Get game configuration by ID
 * @param {string} gameId - The ID of the game
 * @returns {Object|undefined} Game configuration object or undefined if not found
 */
export const getGameConfigById = (gameId) => 
  SORTED_GAMES_CONFIG.find(game => game.id === gameId);

/**
 * Get game configuration by dailyKey (for useDailyGameState compatibility)
 * @param {string} dailyKey - The dailyKey to search for
 * @returns {Object|undefined} Game configuration object or undefined if not found
 */
export const getGameConfigByDailyKey = (dailyKey) =>
  SORTED_GAMES_CONFIG.find(game => game.dailyKey === dailyKey);

/**
 * Create localStorage game configs for useDailyGameState.js
 * Maps game IDs to their dailyKey for localStorage operations.
 * Now derived dynamically from the SSoT instead of hardcoded.
 * @returns {Array} Array of objects with key and dailyKey properties
 */
export const getLocalStorageGameConfigs = () => 
  SORTED_GAMES_CONFIG.map(game => ({ key: game.id, dailyKey: game.dailyKey }));

/**
 * **CRITICAL NEW FUNCTION**
 * Assembles the full configuration object required by the GameEngine component.
 * It merges the static data from our SSoT with the imported logic/components.
 * @param {string} gameId - The ID of the game
 * @returns {Object|null} A complete configuration object or null if not found
 */
export const getGameEngineConfig = (gameId) => {
  const baseConfig = getGameConfigById(gameId);
  if (!baseConfig) {
    console.warn(`Game config not found for ID: ${gameId}`);
    return null;
  }

  // Merge the base config with its specific logic
  const fullConfig = {
    ...baseConfig,
    ...baseConfig.logic,
    // Add backward compatibility aliases
    gameName: baseConfig.title,
    gameId: baseConfig.id,
    dailyGameKey: baseConfig.dailyKey,
  };

  // Remove the logic property since GameEngine doesn't need it
  delete fullConfig.logic;

  return fullConfig;
};

export default SORTED_GAMES_CONFIG;